import PersonSearchOutlined from '@mui/icons-material/PersonSearchOutlined';
import Drawer from 'components/Drawer/Drawer';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React from 'react';
import { BreadcrumbMeta } from 'v2/components/Breadcrumb/Breadcrumb';
import EmptyStateOverview from 'v2/components/EmptyStateOverview/EmptyStateOverview';
import useModalContext from 'v2/components/Modal/useModalContext';
import OverviewContent from 'v2/components/OverviewContent/OverviewContent';
import PageTabs from 'v2/components/PageTabs/PageTabs';
import SearchAndFilters from 'v2/components/SearchAndFilters/SearchAndFilters';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import useVisitorsGroupsCache from 'v2/hooks/useVisitorsGroups/useVisitorsGroupsCache';
import { createUsersTabs } from 'v2/pages/Users/createUserTabs';
import AddUserGroupModal from './components/AddUserGroupModal';
import GroupsOverviewTable from './components/GroupsOverviewTable';
import { useUserGroupSearchAndFilters } from './useUserGroupSearchAndFilters';

const UserGroupOverview: React.FC = () => {
  const { translation } = useTranslation();
  const { showSnackbar } = useSnackbarContext();
  const { openModal } = useModalContext();
  const { allVisitorGroups, loadingAllVisitorGroups: isLoading } = useVisitorsGroupsCache({
    handleFetchError: (msg) => showSnackbar(msg, { severity: 'error' })
  });

  const { filterStr, setFilterStr, filteredVisitorGroups, countCaption, clearAllFilters, chips, sections } =
    useUserGroupSearchAndFilters({ allVisitorGroups });

  const tabs = createUsersTabs(translation);
  const breadcrumb = {
    title: translation.user_groups,
    crumbs: [
      {
        text: translation.users,
        path: '/users'
      }
    ]
  };

  const handleOpenAddVisitorGroupModal = () => {
    openModal(<AddUserGroupModal />, { maxWidth: 900, width: '80vw' });
  };

  return (
    <>
      <BreadcrumbMeta breadcrumb={breadcrumb} />
      <PageTabs tabs={tabs} />
      <SearchAndFilters
        filterStr={filterStr}
        onFilterStrChange={setFilterStr}
        sections={sections}
        chips={chips}
        countCaption={countCaption}
        actionButton={{
          id: 'add-user-group-button',
          label: translation.add_group,
          onClick: handleOpenAddVisitorGroupModal
        }}
        onClearAll={clearAllFilters}
      />
      <OverviewContent
        loading={isLoading}
        hasData={allVisitorGroups.length > 0}
        hasResults={filteredVisitorGroups.length > 0}
        table={<GroupsOverviewTable visitorGroups={filteredVisitorGroups} loading={isLoading} />}
        noResults={
          <EmptyStateOverview
            icon={PersonSearchOutlined}
            title={translation.no_results}
            content={translation.no_results_search_and_filters_description}
          />
        }
        noData={
          <EmptyStateOverview
            icon={PersonSearchOutlined}
            title={translation.no_groups_created}
            content={translation.there_are_no_groups_added}
            button={{ label: translation.add_group, onClick: handleOpenAddVisitorGroupModal }}
          />
        }
      />
      <Drawer />
    </>
  );
};
export default UserGroupOverview;
